import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, BarChart3, Shield, TrendingUp } from "lucide-react";
import HorizontalList from "../components/horizontalList";
import SimuladorCaja from "../components/empresas/gestionDeCaja/SimuladorCaja";
import { useEmpresasLanguage } from "../context/EmpresasLanguageContext";
import empresasHero from "../assets/EmpresasInicio2.jpg";

const impactIcons = [BarChart3, Shield, TrendingUp];

function GestionDeCaja() {
  const { t } = useEmpresasLanguage();
  const g = t.gestionCaja;

  return (
    <div className="overflow-x-hidden bg-slate-50">
      <section className="relative bg-[#0b1f3f] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-15 bg-cover bg-center"
          style={{ backgroundImage: `url(${empresasHero})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0b1f3f] via-[#123a72] to-[#1746a2]" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
          <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.35em] text-[#9ec5ff] mb-4">
            {g.eyebrow}
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">{g.title}</h1>
          <p className="mt-5 text-lg sm:text-xl text-[#dbeafe] max-w-2xl font-medium leading-relaxed">
            {g.subtitle}
          </p>
        </div>
      </section>

      <HorizontalList items={g.nav} even plain />

      <section id="intro" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] leading-snug">{g.introTitle}</h2>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{g.introP1}</p>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">{g.introP2}</p>
          </div>
          <ul className="space-y-3">
            {g.riesgos.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4"
              >
                <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-slate-700 font-medium">{item}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="simulador" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#e8f2f4]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-sm font-semibold uppercase tracking-[0.25em] text-[#1746a2]">{g.simuladorLabel}</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold text-[#0b1f3f]">{g.simuladorTitle}</h2>
            <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{g.simuladorIntro}</p>
          </div>
          <SimuladorCaja />
        </div>
      </section>

      <section id="importancia" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0b1f3f] text-center mb-10">
            {g.importanciaTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {g.importanciaCards.map((card, index) => {
              const Icon = impactIcons[index];
              return (
                <article key={card.titulo} className="rounded-3xl border border-slate-200 p-6 bg-slate-50">
                  <div className="w-11 h-11 rounded-xl bg-[#1746a2]/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-[#1746a2]" />
                  </div>
                  <h3 className="text-xl font-extrabold text-[#0b1f3f]">{card.titulo}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{card.texto}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section id="cfo" className="scroll-mt-24 py-16 px-6 sm:px-10 lg:px-16 bg-[#1746a2] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-extrabold">{g.cfoTitle}</h2>
          <p className="mt-5 text-[#dbeafe] text-lg leading-relaxed">{g.cfoBody}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link
              to="/empresas/direccion-de-innovacion-financiera"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3 text-[#123a72] font-bold hover:bg-[#e8f2ff] transition"
            >
              {g.cfoCta}
              <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={t.cfo.whatsapp.cfo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center rounded-full border-2 border-white/80 px-8 py-3 font-bold hover:bg-white/10 transition"
            >
              {g.cfoWhatsapp}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default GestionDeCaja;
